// THREE vient du bundle-three-vendor (chargé avant celui-ci, cf. app/js/ThreeVendor.js) : une seule
// copie de Three.js partagée entre bundle-home/wave/solutions.
const { THREE } = window.ThreeVendor;

// Nuage de particules animé en vague, calé sur les couleurs du styleguide
// (Color 1 / Color 1 - 30% / Color 2 en hover). Adapté du prototype
// wave.html : mêmes shaders, mais piloté par la taille du conteneur (pas du
// viewport). Valeurs par défaut ci-dessous ; le constructeur accepte un objet
// de surcharge (cf. usages dans BlockTextWave.js) pour obtenir des rendus
// différents (amplitude, turbulence, densité...) sans dupliquer la scène.
const PARAMS = {
	cols: 220,
	rows: 64,
	spacing: 0.45,
	ptBase: 150,
	sizeMin: 5,
	sizeMax: 2.2,
	colorA: "#001DFC",
	colorB: "#B3BCFF",
	hoverColor: "#00FCD9",
	amp: 0.35,
	speed: 0.22,
	freq: 1.0,
	turb: 7,
	hoverStrength: 0.3,
	hoverRadius: 0.27,
	aperture: 0.04,
	planY: 0,
	planZ: 3,
	tiltX: 3,
	tiltY: -3,
	camH: 9,
	lookY: 5,
};

const VERTEX_SHADER = /* glsl */`
	attribute float aSeed;
	uniform float uTime, uPtBase, uSizeMin, uSizeMax;
	uniform float uAmp, uFreq, uTurb, uSpacing;
	uniform float uFocalDepth, uAperture;
	uniform float uHoverStrength, uHoverRadius, uAspect;
	uniform vec2 uMouseNDC;
	uniform vec3 uColorA, uColorB, uHoverColor;
	varying float vAlpha;
	varying vec3 vColor;
	varying float vCoC;

	float waves(vec2 xz, float t) {
		float h = 0.0;
		h += sin(xz.x * 0.18 * uFreq + xz.y * 0.12 * uFreq + t * 1.0) * 5.5;
		if (uTurb >= 2.0) h += sin(xz.x * 0.09 * uFreq - xz.y * 0.22 * uFreq + t * 0.65) * 4.2;
		if (uTurb >= 3.0) h += sin(xz.x * 0.32 * uFreq + xz.y * 0.07 * uFreq - t * 0.85) * 2.8;
		if (uTurb >= 4.0) h += sin(xz.x * 0.06 * uFreq + xz.y * 0.31 * uFreq + t * 0.45) * 6.5;
		if (uTurb >= 5.0) h += sin(xz.x * 0.24 * uFreq - xz.y * 0.16 * uFreq + t * 1.3) * 1.8;
		if (uTurb >= 6.0) h += sin(xz.x * 0.41 * uFreq + xz.y * 0.28 * uFreq - t * 0.55) * 3.2;
		return h * uAmp;
	}

	void main() {
		vec3 p = position;
		p.x *= uSpacing;
		p.z *= uSpacing;
		p.y = waves(p.xz, uTime);

		p.x += sin(uTime * 0.7 + aSeed * 29.3) * 0.04;
		p.z += cos(uTime * 0.6 + aSeed * 13.7) * 0.04;

		vec4 preClip = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
		vec2 ndc = preClip.xy / preClip.w;
		vec2 delta = uMouseNDC - ndc;
		delta.x *= uAspect;
		float screenDist = length(delta);
		float hover = 1.0 - smoothstep(0.0, uHoverRadius, screenDist);
		p.y += hover * uHoverStrength * 6.0;

		vec4 mvPos = modelViewMatrix * vec4(p, 1.0);
		float depth = -mvPos.z;

		float coc = clamp(abs(depth - uFocalDepth) * uAperture, 0.0, 1.0);
		vCoC = coc;

		float sz = uPtBase * (0.6 + aSeed * 0.4) / max(depth, 1.0);
		sz = sz * (1.0 + coc * 4.0);
		gl_PointSize = clamp(sz, uSizeMin, uSizeMax * (1.0 + coc * 3.0));
		gl_Position = projectionMatrix * mvPos;

		float h01 = clamp(p.y / 4.0 * 0.5 + 0.5, 0.0, 1.0);
		vec3 baseColor = mix(uColorA, uColorB, h01);
		vColor = mix(baseColor, uHoverColor + 0.3, hover * 0.85);
		vAlpha = (0.6 + aSeed * 0.38) * (1.0 - coc * 0.72) + hover * 0.35;
	}
`;

const FRAGMENT_SHADER = /* glsl */`
	varying float vAlpha;
	varying vec3 vColor;
	varying float vCoC;
	void main() {
		float d = length(gl_PointCoord - vec2(0.5));
		if (d > 0.5) discard;
		float sharpness = mix(2.8, 0.9, vCoC);
		float soft = pow(1.0 - d * 2.0, sharpness);
		gl_FragColor = vec4(vColor, vAlpha * soft);
	}
`;

export default class TextWaveScene {
	constructor(container, paramsOverride = {}) {
		this.container = container;
		this.params = { ...PARAMS, ...paramsOverride };
		this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		this.canvas = document.createElement("canvas");
		container.appendChild(this.canvas);

		this.mouseNDC = new THREE.Vector2(-10, -10);
		this.smoothMouseNDC = new THREE.Vector2(-10, -10);
		this._centerMV = new THREE.Vector3();
		this.clock = new THREE.Clock();
		this.visible = true;

		this._initScene();
		this._buildGrid();
		this._bindEvents();

		if (this.reducedMotion) {
			this._render();
		} else {
			this._tick = this._tick.bind(this);
			this._raf = requestAnimationFrame(this._tick);
		}
	}

	_initScene() {
		const { width, height } = this._size();

		this.scene = new THREE.Scene();
		this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 500);
		this.camera.position.set(0, this.params.camH, 40);
		this.camera.lookAt(0, this.params.lookY, -15);

		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(width, height, false);
	}

	_buildGrid() {
		const p = this.params;
		const N = p.cols * p.rows;
		const pos = new Float32Array(N * 3);
		const seed = new Float32Array(N);
		const totalW = p.cols * 0.6;
		const totalD = p.rows * 0.6;

		let idx = 0;
		for (let ci = 0; ci < p.cols; ci++) {
			for (let ri = 0; ri < p.rows; ri++) {
				pos[idx * 3] = (ci / (p.cols - 1) - 0.5) * totalW;
				pos[idx * 3 + 1] = 0;
				pos[idx * 3 + 2] = 18 - (ri / (p.rows - 1)) * totalD;
				seed[idx] = Math.random();
				idx++;
			}
		}

		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(pos, 3));
		geometry.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));

		const { width, height } = this._size();

		const material = new THREE.ShaderMaterial({
			uniforms: {
				uTime: { value: 0 },
				uPtBase: { value: p.ptBase },
				uSizeMin: { value: p.sizeMin },
				uSizeMax: { value: p.sizeMax },
				uColorA: { value: new THREE.Color(p.colorA) },
				uColorB: { value: new THREE.Color(p.colorB) },
				uAmp: { value: p.amp },
				uFreq: { value: p.freq },
				uTurb: { value: p.turb },
				uMouseNDC: { value: new THREE.Vector2(-10, -10) },
				uSpacing: { value: p.spacing },
				uAspect: { value: width / height },
				uHoverColor: { value: new THREE.Color(p.hoverColor) },
				uHoverStrength: { value: this.reducedMotion ? 0 : p.hoverStrength },
				uHoverRadius: { value: p.hoverRadius },
				uFocalDepth: { value: 45 },
				uAperture: { value: p.aperture },
			},
			vertexShader: VERTEX_SHADER,
			fragmentShader: FRAGMENT_SHADER,
			transparent: true,
			blending: THREE.NormalBlending,
			depthWrite: false,
		});

		this.points = new THREE.Points(geometry, material);
		this.points.rotation.x = THREE.MathUtils.degToRad(p.tiltX);
		this.points.rotation.y = THREE.MathUtils.degToRad(p.tiltY);
		this.points.position.y = p.planY;
		this.points.position.z = p.planZ;
		this.scene.add(this.points);
	}

	_bindEvents() {
		this._onMouseMove = (e) => {
			const rect = this.container.getBoundingClientRect();
			this.mouseNDC.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
			this.mouseNDC.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
		};
		if (!this.reducedMotion) {
			window.addEventListener("pointermove", this._onMouseMove);
		}

		this._resizeObserver = new ResizeObserver(() => this._resize());
		this._resizeObserver.observe(this.container);

		this._intersectionObserver = new IntersectionObserver(([entry]) => {
			this.visible = entry.isIntersecting;
		});
		this._intersectionObserver.observe(this.container);
	}

	_size() {
		const width = this.container.clientWidth || 1;
		const height = this.container.clientHeight || 1;
		return { width, height };
	}

	_resize() {
		const { width, height } = this._size();
		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(width, height, false);
		this.points.material.uniforms.uAspect.value = width / height;
		if (this.reducedMotion) this._render();
	}

	_render() {
		const u = this.points.material.uniforms;
		// Vecteur réutilisé : ce calcul tourne à chaque frame, et la page "à propos"
		// instancie trois de ces scènes (bloc Cards) — autant d'allocations par frame
		// poussées au GC pour rien.
		const centerMV = this._centerMV.set(0, this.params.planY, -10).applyMatrix4(this.camera.matrixWorldInverse);
		u.uFocalDepth.value = -centerMV.z;
		this.renderer.render(this.scene, this.camera);
	}

	_tick() {
		this._raf = requestAnimationFrame(this._tick);
		if (!this.visible) return;

		this.points.material.uniforms.uTime.value = this.clock.getElapsedTime() * this.params.speed;
		this.smoothMouseNDC.lerp(this.mouseNDC, 0.1);
		this.points.material.uniforms.uMouseNDC.value.copy(this.smoothMouseNDC);

		this._render();
	}
}
